<template>
  <div class="personal_data_wrapper fadeIn">
    <div class="personal-data-container" v-if="userInfo">
      <!-- Top Header Action & Summary Bar -->
      <div class="modern-card profile-hero-bar mb-4">
        <div
          class="d-flex flex-wrap justify-content-between align-items-center gap-3"
        >
          <!-- Profile Badge Info -->
          <div class="d-flex align-items-center gap-3">
            <div class="company-avatar-box">
              <i class="far fa-building"></i>
            </div>
            <div>
              <h5 class="font-weight-bold mb-1 text-dark">
                {{ userInfo.full_name }}
              </h5>
              <div class="d-flex align-items-center gap-2">
                <span
                  class="status-chip"
                  :class="userInfo.is_active ? 'chip-active' : 'chip-inactive'"
                >
                  <span class="pulse-dot"></span>
                  {{ userInfo.is_active ? "حساب مفعل" : "حساب غير مفعل" }}
                </span>
                <span
                  class="status-chip ml-2"
                  :class="userInfo.is_ban ? 'chip-banned' : 'chip-unbanned'"
                >
                  {{ userInfo.is_ban ? "محظور" : "غير محظور" }}
                </span>
              </div>
            </div>
          </div>

          <!-- Ban / Unban Toggle Button -->
          <div>
            <v-btn
              depressed
              @click="toggleStatusModel"
              class="custom-hero-btn text-white rounded-pill px-5"
              :color="userInfo.is_ban ? 'success' : 'error'"
            >
              <i
                :class="
                  userInfo.is_ban
                    ? 'far fa-user-check ml-2'
                    : 'far fa-user-slash ml-2'
                "
              ></i>
              {{ userInfo.is_ban ? "فك حظر الشركة" : "حظر الشركة" }}
            </v-btn>
          </div>
        </div>
      </div>

      <!-- Quick Metrics KPI Grid -->
      <div class="row mb-4" v-if="userInfo.company_wallet">
        <div class="col-6 col-md-3">
          <div class="kpi-stat-card">
            <div class="kpi-icon-box icon-emerald">
              <i class="far fa-wallet"></i>
            </div>
            <div class="kpi-info">
              <span class="kpi-label">رصيد المحفظة</span>
              <h5 class="kpi-value text-emerald mb-0 font-weight-bold">
                {{ userInfo.company_wallet.balance || 0 }}
              </h5>
            </div>
          </div>
        </div>

        <div class="col-6 col-md-3">
          <div class="kpi-stat-card">
            <div class="kpi-icon-box icon-purple">
              <i class="far fa-shopping-bag"></i>
            </div>
            <div class="kpi-info">
              <span class="kpi-label">عدد الطلبات الإجمالي</span>
              <h5 class="kpi-value mb-0 font-weight-bold">
                {{ userInfo.company_wallet.orders_count || 0 }}
              </h5>
            </div>
          </div>
        </div>

        <div class="col-6 col-md-3">
          <div class="kpi-stat-card">
            <div class="kpi-icon-box icon-blue">
              <i class="far fa-coins"></i>
            </div>
            <div class="kpi-info">
              <span class="kpi-label">إجمالي قيمة الطلبات</span>
              <h5 class="kpi-value mb-0 font-weight-bold">
                {{ userInfo.company_wallet.orders_total || 0 }}
              </h5>
            </div>
          </div>
        </div>

        <div class="col-6 col-md-3">
          <div class="kpi-stat-card">
            <div class="kpi-icon-box icon-orange">
              <i class="far fa-users-crown"></i>
            </div>
            <div class="kpi-info">
              <span class="kpi-label">عدد السائقين</span>
              <h5 class="kpi-value mb-0 font-weight-bold">
                {{ userInfo.drivers_count || 0 }}
              </h5>
            </div>
          </div>
        </div>
      </div>

      <!-- Main Profile Cards Grid -->
      <div class="row">
        <!-- Company Personal Details Card -->
        <div class="col-lg-6 mb-4">
          <div class="modern-card h-100">
            <div class="card-header-sm">
              <h6 class="title mb-0">
                <i class="far fa-id-card ml-2 text-primary"></i>
                بيانات الشركة والاتصال
              </h6>
            </div>
            <div class="card-body-sm">
              <div class="profile-info-grid">
                <div class="info-cell">
                  <div class="cell-icon text-primary">
                    <i class="far fa-building"></i>
                  </div>
                  <div class="cell-content">
                    <span class="cell-label">{{
                      $t("forms.labels.name")
                    }}</span>
                    <span class="cell-value">{{
                      userInfo.full_name || "-"
                    }}</span>
                  </div>
                </div>

                <div class="info-cell" v-if="userInfo.phone">
                  <div class="cell-icon text-success">
                    <i class="far fa-phone"></i>
                  </div>
                  <div class="cell-content">
                    <span class="cell-label">{{
                      $t("forms.labels.phone")
                    }}</span>
                    <a
                      :href="`tel:${userInfo.phone}`"
                      class="cell-value phone-link dir-ltr"
                    >
                      +{{ userInfo.phone_code }} {{ userInfo.phone }}
                    </a>
                  </div>
                </div>

                <div class="info-cell" v-if="userInfo.email">
                  <div class="cell-icon text-info">
                    <i class="far fa-envelope"></i>
                  </div>
                  <div class="cell-content">
                    <span class="cell-label">{{
                      $t("forms.labels.email")
                    }}</span>
                    <a
                      :href="`mailto:${userInfo.email}`"
                      class="cell-value email-link"
                    >
                      {{ userInfo.email }}
                    </a>
                  </div>
                </div>

                <div class="info-cell" v-if="userInfo.country">
                  <div class="cell-icon text-warning">
                    <i class="far fa-globe"></i>
                  </div>
                  <div class="cell-content">
                    <span class="cell-label">{{
                      $t("forms.labels.country")
                    }}</span>
                    <span class="cell-value">{{ userInfo.country.name }}</span>
                  </div>
                </div>

                <div class="info-cell">
                  <div class="cell-icon text-purple">
                    <i class="far fa-user-check"></i>
                  </div>
                  <div class="cell-content">
                    <span class="cell-label">حالة التفعيل</span>
                    <span
                      class="cell-value font-weight-bold"
                      :class="
                        userInfo.is_active ? 'text-success' : 'text-danger'
                      "
                    >
                      {{ userInfo.is_active ? "مفعل" : "غير مفعل" }}
                    </span>
                  </div>
                </div>

                <div class="info-cell">
                  <div class="cell-icon text-danger">
                    <i class="far fa-user-lock"></i>
                  </div>
                  <div class="cell-content">
                    <span class="cell-label">حالة الحظر</span>
                    <span
                      class="cell-value font-weight-bold"
                      :class="userInfo.is_ban ? 'text-danger' : 'text-success'"
                    >
                      {{ userInfo.is_ban ? "محظور" : "غير محظور" }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Wallet & Wallet Operations Details Card -->
        <div class="col-lg-6 mb-4" v-if="userInfo.company_wallet">
          <div class="modern-card h-100">
            <div class="card-header-sm">
              <h6 class="title mb-0">
                <i class="far fa-chart-line-up ml-2 text-primary"></i>
                إحصائيات إضافية وحظر
              </h6>
            </div>
            <div class="card-body-sm">
              <div class="profile-info-grid">
                <div class="info-cell">
                  <div class="cell-icon text-danger">
                    <i class="far fa-times-circle"></i>
                  </div>
                  <div class="cell-content">
                    <span class="cell-label">عدد الطلبات الملغاة</span>
                    <span class="cell-value font-weight-bold text-danger">
                      {{ userInfo.company_wallet.rejected_count || 0 }}
                    </span>
                  </div>
                </div>

                <div class="info-cell">
                  <div class="cell-icon text-warning">
                    <i class="far fa-gift"></i>
                  </div>
                  <div class="cell-content">
                    <span class="cell-label">عدد البونص لمناديب الشركة</span>
                    <span class="cell-value font-weight-bold text-dark">
                      {{ userInfo.company_wallet.bonus_count || 0 }}
                    </span>
                  </div>
                </div>

                <div class="info-cell">
                  <div class="cell-icon text-secondary">
                    <i class="far fa-steering-wheel"></i>
                  </div>
                  <div class="cell-content">
                    <span class="cell-label">عدد السائقين المسجلين</span>
                    <span class="cell-value">{{
                      userInfo.drivers_count || 0
                    }}</span>
                  </div>
                </div>
              </div>

              <!-- Ban Reason Warning Box -->
              <div
                class="ban-reason-box mt-4 p-3"
                v-if="userInfo.is_ban && userInfo.ban_reason"
              >
                <div class="d-flex align-items-start gap-2">
                  <i
                    class="far fa-exclamation-circle text-danger fa-lg mt-1"
                  ></i>
                  <div>
                    <h6 class="font-weight-bold text-danger mb-1">
                      سبب الحظر:
                    </h6>
                    <p class="mb-0 text-dark small font-weight-bold">
                      {{ userInfo.ban_reason }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Status Ban / Unban Modal Dialog -->
    <v-dialog v-model="statusModel" width="480">
      <v-card class="modern-dialog-card p-4" v-if="userInfo">
        <div class="text-center mb-3">
          <div
            class="dialog-icon-circle mx-auto mb-2"
            :class="
              userInfo.is_ban
                ? 'bg-success-light text-success'
                : 'bg-danger-light text-danger'
            "
          >
            <i
              :class="
                userInfo.is_ban
                  ? 'far fa-lock-open-alt fa-2x'
                  : 'far fa-user-slash fa-2x'
              "
            ></i>
          </div>
          <h5 class="font-weight-bold text-dark mb-1" v-if="userInfo.is_ban">
            هل تود فك حظر هذه الشركة؟
          </h5>
          <h5 class="font-weight-bold text-dark mb-1" v-else>
            هل تود حظر هذه الشركة؟
          </h5>
        </div>

        <div class="input_wrapper top_label my-4" v-if="!userInfo.is_ban">
          <textarea
            v-model="ban_reason"
            placeholder="أدخل سبب الحظر..."
            class="form-control custom-textarea-field"
            rows="3"
          ></textarea>
          <label class="form-label">سبب الحظر*</label>
        </div>

        <div class="d-flex justify-content-center gap-3 mt-4">
          <v-btn
            depressed
            class="px-8 rounded-pill text-white"
            @click="changeStatus"
            :loading="statusLoading"
            :color="userInfo.is_ban ? 'success' : 'error'"
          >
            تأكيد
          </v-btn>
          <v-btn
            depressed
            class="px-8 rounded-pill"
            @click="statusModel = false"
          >
            إلغاء
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  props: ["userInfo"],

  data() {
    return {
      center: {},
      markers: null,
      coordinates_to_edit: { lat: 0, lng: 0 },
      ban_reason: null,
      statusLoading: false,
      acceptLoading: false,
      acceptModel: false,
      statusModel: false,
    };
  },

  computed: {
    isActive() {
      return this.userInfo.is_active ? this.$t("active") : this.$t("inactive");
    },
    isBan() {
      return this.userInfo.is_ban ? this.$t("ban") : this.$t("inban");
    },
    isActiveManager() {
      return this.userInfo.is_ban ? "غير مفعل" : "مفعل";
    },
  },

  methods: {
    getLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition((position) => {
          this.markers = [
            {
              position: {
                lat: position.coords.latitude,
                lng: position.coords.longitude,
              },
            },
          ];
          this.center = this.markers[0].position;
          this.coordinates_to_edit.lat = this.center.lat;
          this.coordinates_to_edit.lng = this.center.lng;
        });
      }
    },
    toggleStatusModel() {
      this.statusModel = true;
    },
    changeStatus() {
      if (!this.userInfo.is_ban && !this.ban_reason) {
        this.$iziToast.error({
          message: "يرجي ادخال سبب الحظر اولا",
          timeout: 5000,
          position: "bottomRight",
        });
        return;
      }
      this.statusLoading = true;
      const formData = new FormData();
      formData.append("is_active", this.userInfo.is_ban ? 0 : 1);
      if (this.ban_reason) {
        formData.append("message", this.ban_reason);
      }
      formData.append("_method", "PUT");

      this.$axios({
        method: "POST",
        url: `/company/is_active/${this.userInfo.id}`,
        data: formData,
      })
        .then((res) => {
          this.$iziToast.success({
            timeout: 5000,
            message: res.data.message,
            position: "bottomRight",
          });
          this.statusModel = false;
          this.$emit("get_item");
          this.ban_reason = null;
          this.statusLoading = false;
        })
        .catch(() => {
          this.statusLoading = false;
        });
    },
  },
  created() {
    this.getLocation();
  },
};
</script>

<style lang="scss" scoped>
.personal_data_wrapper {
  padding: 8px 0;

  /* Top Hero Bar */
  .profile-hero-bar {
    background: #ffffff;
    border-radius: 20px;
    padding: 20px 24px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0, 0, 0, 0.03);

    .company-avatar-box {
      width: 56px;
      height: 56px;
      border-radius: 16px;
      background: linear-gradient(
        135deg,
        rgba(120, 61, 115, 0.12),
        rgba(120, 61, 115, 0.04)
      );
      color: var(--mainColor-2, #783d73);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 24px;
      box-shadow: 0 6px 16px rgba(120, 61, 115, 0.1);
    }

    .status-chip {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 14px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 700;

      .pulse-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
      }

      &.chip-active {
        background: rgba(16, 185, 129, 0.1);
        color: #10b981;
        .pulse-dot {
          background-color: #10b981;
        }
      }
      &.chip-inactive {
        background: rgba(100, 116, 139, 0.1);
        color: #64748b;
        .pulse-dot {
          background-color: #64748b;
        }
      }
      &.chip-banned {
        background: rgba(239, 68, 68, 0.1);
        color: #ef4444;
      }
      &.chip-unbanned {
        background: rgba(16, 185, 129, 0.1);
        color: #10b981;
      }
    }

    .custom-hero-btn {
      height: 42px !important;
      font-weight: 700;
      font-size: 13px;
      box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08) !important;
      transition: transform 0.2s ease, box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 18px rgba(0, 0, 0, 0.12) !important;
      }
    }
  }

  /* KPI Stat Cards */
  .kpi-stat-card {
    background: #ffffff;
    border-radius: 18px;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0, 0, 0, 0.03);
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      transform: translateY(-3px);
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.06);
    }

    .kpi-icon-box {
      width: 50px;
      height: 50px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      flex-shrink: 0;

      &.icon-emerald {
        background: rgba(16, 185, 129, 0.1);
        color: #10b981;
      }
      &.icon-purple {
        background: rgba(120, 61, 115, 0.1);
        color: #783d73;
      }
      &.icon-blue {
        background: rgba(14, 165, 233, 0.1);
        color: #0ea5e9;
      }
      &.icon-orange {
        background: rgba(245, 158, 11, 0.1);
        color: #f59e0b;
      }
    }

    .kpi-info {
      .kpi-label {
        font-size: 12px;
        color: #94a3b8;
        display: block;
        margin-bottom: 2px;
      }
      .kpi-value {
        font-size: 17px;
        color: #1e293b;

        &.text-emerald {
          color: #10b981;
        }
      }
    }
  }

  /* Modern Card Container */
  .modern-card {
    background: #ffffff;
    border-radius: 20px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03);
    border: 1px solid rgba(0, 0, 0, 0.03);
    overflow: hidden;

    .card-header-sm {
      padding: 16px 20px;
      background: linear-gradient(
        135deg,
        rgba(120, 61, 115, 0.04) 0%,
        rgba(120, 61, 115, 0.01) 100%
      );
      border-bottom: 1px solid rgba(0, 0, 0, 0.04);

      .title {
        font-size: 15px;
        font-weight: 700;
        color: #1e293b;
      }
    }

    .card-body-sm {
      padding: 20px;
    }
  }

  /* Profile Info Cells */
  .profile-info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 16px;

    .info-cell {
      background: #f8fafc;
      padding: 12px 16px;
      border-radius: 14px;
      display: flex;
      align-items: center;
      gap: 14px;
      border: 1px solid rgba(0, 0, 0, 0.02);

      .cell-icon {
        width: 40px;
        height: 40px;
        border-radius: 12px;
        background: #ffffff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        flex-shrink: 0;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
      }

      .cell-content {
        overflow: hidden;

        .cell-label {
          font-size: 11px;
          color: #94a3b8;
          display: block;
          margin-bottom: 2px;
        }

        .cell-value {
          font-size: 13px;
          font-weight: 700;
          color: #1e293b;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          display: block;

          &.phone-link,
          &.email-link {
            text-decoration: none;
            transition: opacity 0.2s ease;
            &:hover {
              opacity: 0.8;
            }
          }
        }
      }
    }
  }

  /* Ban Reason Alert Box */
  .ban-reason-box {
    background: rgba(239, 68, 68, 0.06);
    border: 1px dashed rgba(239, 68, 68, 0.3);
    border-radius: 14px;
  }

  /* Dialog Enhancements */
  .modern-dialog-card {
    border-radius: 24px !important;

    .dialog-icon-circle {
      width: 60px;
      height: 60px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;

      &.bg-success-light {
        background: rgba(16, 185, 129, 0.12);
        color: #10b981;
      }
      &.bg-danger-light {
        background: rgba(239, 68, 68, 0.12);
        color: #ef4444;
      }
    }

    .custom-textarea-field {
      border-radius: 12px;
      border: 1px solid rgba(0, 0, 0, 0.15);
      padding: 12px 14px;
      font-size: 14px;

      &:focus {
        border-color: #783d73;
        box-shadow: 0 0 0 3px rgba(120, 61, 115, 0.15);
      }
    }
  }

  .gap-2 {
    gap: 8px;
  }
  .gap-3 {
    gap: 14px;
  }
  .dir-ltr {
    direction: ltr;
  }
}
</style>
